/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { FormControl, Grid, TextField } from '@mui/material';
import { RetailerDeliveryLocationModel } from '../../../../../app/services/api/ofdClient';
import LeavePageModal from '../../../../../components/shared/dialogs/LeavePageModal';
import MapsAutoComplete from '../MapsAutoComplete';

interface SubmitModalProps {
  openModal: boolean;
  onConfirm: Function;
  selectedLocation: RetailerDeliveryLocationModel;
  onCancel: () => void;
}

const CreateEditModal: React.FC<SubmitModalProps> = ({ openModal, onCancel, selectedLocation, onConfirm }) => {
  const { t } = useTranslation('onboardingWizard');
  const isCreate = selectedLocation.id == 0;
  const textFieldMargin = { mb: 8 };
  const [editedLocation, setEditedLocation] = React.useState<RetailerDeliveryLocationModel>({ ...selectedLocation });
  const [showLeaveModal, setShowLeaveModal] = React.useState<boolean>(false);

  React.useEffect(() => {
    setEditedLocation({ ...selectedLocation });
  }, [selectedLocation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
    setEditedLocation({ ...editedLocation, [fieldName]: e.target.value });
  };

  const updateAddressFields = async (place: any) => {
    const componentMap: Record<string, string> = {};
    place.forEach((component: { types: any[]; long_name: string; short_name: string }) => {
      component.types.forEach((type) => {
        componentMap[type] = component.long_name;
        if (type === 'country') {
          componentMap['country_code'] = component.short_name;
        }
      });
    });

    const addressLine1 = componentMap['street_number']
      ? `${componentMap['street_number']} ${componentMap['route']}`
      : componentMap['route'] || '';
    const addressLine2 =
      componentMap['sublocality'] ||
      componentMap['locality'] ||
      componentMap['neighborhood'] ||
      '';
    const city =
      componentMap['postal_town'] ||
      componentMap['locality'] ||
      componentMap['administrative_area_level_2'] ||
      '';
    const country = componentMap['country'] || '';
    const postalCode = componentMap['postal_code'] || '';

    setEditedLocation((prevData: RetailerDeliveryLocationModel) => ({
      ...prevData,
      addressLine1,
      addressLine2,
      city,
      country,
      postalCode,
    }));
  };

  const isFormDirty = () => {
    if (!isFormValid()) return false;
    return JSON.stringify(editedLocation) !== JSON.stringify(selectedLocation);
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    }
    else {
      onCancel();
    }
  };

  const onSave = () => {
    setShowLeaveModal(false);
    onConfirm(editedLocation);
  };

  const isFormValid = () => {
    return !!(
      editedLocation.name?.trim() &&
      editedLocation.addressLine1?.trim() &&
      editedLocation.city?.trim() &&
      editedLocation.country?.trim() &&
      editedLocation.postalCode?.trim() &&
      editedLocation.contactName?.trim() &&
      editedLocation.contactNumber?.trim()
    );
  };

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleCancel}
        maxWidth='md'>
        <DialogTitle sx={{ m: 4, p: 4 }}>
          {isCreate ? t('deliveryLocationsTab.createDeliveryLocation') : t('deliveryLocationsTab.editDeliveryLocation')}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container
            spacing={3}
            sx={{ pt: '10px' }}
          >
            <Grid item
              xs={12}>
              <FormControl fullWidth>
                <TextField
                  required
                  label={t('deliveryLocationsTab.name')}
                  value={editedLocation.name}
                  onChange={(e) => handleChange(e, 'name')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              xs={12}>
              <FormControl fullWidth>
                <MapsAutoComplete
                  address={editedLocation.addressLine1 ?? ''}
                  onAddressSelect={updateAddressFields}
                  setNewAddressInput={(value: string) => setEditedLocation(prev => ({ ...prev, addressLine1: value }))}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  label={t('deliveryLocationsTab.addressLine2')}
                  value={editedLocation.addressLine2}
                  onChange={(e) => handleChange(e, 'addressLine2')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  required
                  label={t('deliveryLocationsTab.postalCode')}
                  value={editedLocation.postalCode}
                  onChange={(e) => handleChange(e, 'postalCode')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  required
                  label={t('deliveryLocationsTab.city')}
                  value={editedLocation.city}
                  onChange={(e) => handleChange(e, 'city')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  required
                  label={t('deliveryLocationsTab.country')}
                  value={editedLocation.country}
                  onChange={(e) => handleChange(e, 'country')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  required
                  label={t('deliveryLocationsTab.contactName')}
                  value={editedLocation.contactName}
                  onChange={(e) => handleChange(e, 'contactName')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
            <Grid item
              sm={12}
              md={6}
            >
              <FormControl fullWidth
                size="small">
                <TextField fullWidth
                  required
                  label={t('deliveryLocationsTab.contactNumber')}
                  value={editedLocation.contactNumber}
                  onChange={(e) => handleChange(e, 'contactNumber')}
                  sx={textFieldMargin} />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!isFormValid()}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={onSave}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
        }} />
    </>
  );
};
export default CreateEditModal;